import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Avatar } from '../components/Avatar';
import { PalatePanel } from '../components/PalatePanel';
import { WinesBrought } from '../components/WinesBrought';
import { MemberComms } from '../components/MemberComms';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  can,
  ROLES,
  ROLE_LABELS,
  MEMBER_TYPES,
  MEMBER_TYPE_LABELS,
  memberTypeLabel,
  ACCOUNT_STATUSES,
  DUES_STATUSES,
  type Role,
  type MemberType,
  type DuesStatus,
} from '@gwc/shared';
import { membersApi, type MemberPatch, type ChargeDuesInput } from '../api/members';
import type { MemberRow, MemberStats, DueRow } from '../api/models';
import { useAuth } from '../auth/AuthProvider';
import { ApiError } from '../lib/api';
import { StatTile } from '../components/Widgets';

const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export function MemberDetail() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const canManage = user ? can(user.roles, 'users:manage') : false;
  const canComms = user ? can(user.roles, 'comms:send') : false;
  const isSelf = user?.id === id;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['members', id],
    queryFn: () => membersApi.get(id),
  });

  if (isLoading) return <p className="text-cream-300/60">Loading…</p>;
  if (isError || !data) return <p className="text-wine-300">Member not found.</p>;

  const { member, stats } = data;

  return (
    <div className="space-y-6">
      <Link to="/members" className="text-sm text-cream-300/60 hover:text-gold-200">
        ← All members
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-4">
          <Avatar
            firstName={member.firstName}
            lastName={member.lastName}
            avatarUrl={member.avatarUrl}
            size="xl"
          />
        <div>
          <h1 className="font-display text-3xl text-cream-50">
            {member.firstName} {member.lastName}
          </h1>
          <p className="mt-1 text-sm text-cream-300/60">
            <span>{memberTypeLabel(member.memberType) ?? '—'}</span>
            {member.memberSince ? ` · Member since ${new Date(member.memberSince + 'T00:00:00').getFullYear()}` : ''}
            {' · '}
            {member.accountStatus ?? 'no login'}
          </p>
        </div>
        </div>
      </div>

      <ActivityRow stats={stats} />

      {/* The palate. The single best answer to "why open this between tastings?" */}
      <section className="space-y-3">
        <h2 className="font-display text-xl text-cream-50">The palate</h2>
        <PalatePanel memberId={member.id} name={`${member.firstName} ${member.lastName}`} />
        <WinesBrought memberId={member.id} />
      </section>

      {canManage && member.accountStatus === 'invited' && (
        <InvitationCard member={member} />
      )}
      {canManage && !member.accountStatus && <InviteExistingCard member={member} />}
      {canManage && member.accountStatus === 'active' && <PasswordResetCard member={member} />}

      <ProfileCard member={member} canManage={canManage} />
      {canManage && <RolesCard member={member} />}
      {(canManage || isSelf) && <DuesCard personId={member.id} readOnly={!canManage} />}
      {canComms && <MemberComms personId={member.id} />}
    </div>
  );
}

function ActivityRow({ stats }: { stats: MemberStats }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatTile label="Tastings Scored" value={stats.eventsScored} />
      <StatTile label="Wines Scored" value={stats.winesScored} />
      <StatTile label="Wines Brought" value={stats.winesBrought} accent="wine" />
      <StatTile label="Tastings Attended" value={stats.eventsAttended} accent="wine" />
    </div>
  );
}

function InvitationCard({ member }: { member: MemberRow }) {
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const resend = useMutation({
    mutationFn: () => membersApi.resendInvite(member.id),
    onSuccess: (res) => {
      setLink(res.acceptUrl);
      setError(res.emailSent ? null : 'Email could not be delivered — share the link below.');
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not resend invitation'),
  });

  return (
    <div className="card border-gold-600/25 bg-ink-900/30">
      <h2 className="text-sm uppercase tracking-[0.15em] text-gold-400">Pending invitation</h2>
      <p className="mt-2 text-sm text-cream-300/70">
        {member.firstName} has not accepted their invitation yet
        {member.email ? ` (${member.email})` : ''}. Resend the email or copy a fresh link below.
      </p>
      {error && <p className="mt-3 text-sm text-wine-300">{error}</p>}
      {link && (
        <p className="mt-3 break-all rounded border border-ink-800 bg-ink-950/50 p-2 font-mono text-xs text-cream-200">
          {link}
        </p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => {
            setError(null);
            resend.mutate();
          }}
          disabled={resend.isPending || !member.email}
          className="btn-primary"
        >
          {resend.isPending ? 'Sending…' : sent ? 'Sent ✓' : 'Resend invitation email'}
        </button>
        {!member.email && (
          <span className="text-xs text-cream-300/50">Add an email address before resending.</span>
        )}
      </div>
    </div>
  );
}

function PasswordResetCard({ member }: { member: MemberRow }) {
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const send = useMutation({
    mutationFn: () => membersApi.sendPasswordReset(member.id),
    onSuccess: (res) =>
      setMessage(
        res.emailSent
          ? { ok: true, text: `Reset link sent to ${res.email}. It expires in one hour.` }
          : { ok: false, text: 'Email could not be delivered. Check the address and try again.' },
      ),
    onError: (e) => setMessage({ ok: false, text: e instanceof ApiError ? e.message : 'Could not send the reset email' }),
  });

  return (
    <div className="card">
      <h2 className="text-sm uppercase tracking-[0.15em] text-gold-400">Account access</h2>
      <p className="mt-2 text-sm text-cream-300/70">
        Locked out? Email {member.firstName} a link to choose a new password
        {member.email ? ` (${member.email})` : ''}. The link goes only to their inbox.
      </p>
      {message && <p className={`mt-3 text-sm ${message.ok ? 'text-gold-200' : 'text-wine-300'}`}>{message.text}</p>}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => {
            setMessage(null);
            send.mutate();
          }}
          disabled={send.isPending || !member.email}
          className="btn-ghost"
        >
          {send.isPending ? 'Sending…' : 'Send password reset email'}
        </button>
        {!member.email && <span className="text-xs text-cream-300/50">Add an email address first.</span>}
      </div>
    </div>
  );
}

function ProfileCard({ member, canManage }: { member: MemberRow; canManage: boolean }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<MemberPatch>({
    firstName: member.firstName,
    lastName: member.lastName,
    email: member.email ?? '',
    phone: member.phone ?? '',
    memberType: (member.memberType as MemberType) ?? null,
    memberSince: member.memberSince ?? '',
    accountStatus: member.accountStatus ?? '',
    notes: member.notes ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      const patch: MemberPatch = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email || null,
        phone: form.phone || null,
        memberType: form.memberType || null,
        memberSince: form.memberSince || null,
        notes: form.notes || null,
      };
      if (form.accountStatus) patch.accountStatus = form.accountStatus;
      return membersApi.update(member.id, patch);
    },
    onSuccess: () => {
      setSaved(true);
      setError(null);
      void qc.invalidateQueries({ queryKey: ['members'] });
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save'),
  });

  const set = (patch: Partial<MemberPatch>) => setForm((f) => ({ ...f, ...patch }));

  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  if (!canManage) {
    return (
      <div className="card">
        <h2 className="mb-4 text-sm uppercase tracking-[0.15em] text-gold-400">Contact</h2>
        <dl className="grid gap-3 sm:grid-cols-2 text-sm">
          <Field label="Email" value={member.email} />
          <Field label="Phone" value={member.phone} />
        </dl>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm uppercase tracking-[0.15em] text-gold-400">Member details</h2>
        {saved && <span className="text-xs text-gold-200">Saved ✓</span>}
      </div>
      {error && <p className="text-sm text-wine-300">{error}</p>}
      <div className="grid gap-4 sm:grid-cols-2">
        <Labeled label="First name">
          <input className="field" value={form.firstName ?? ''} onChange={(e) => set({ firstName: e.target.value })} required />
        </Labeled>
        <Labeled label="Last name">
          <input className="field" value={form.lastName ?? ''} onChange={(e) => set({ lastName: e.target.value })} required />
        </Labeled>
        <Labeled label="Email">
          <input className="field" type="email" value={form.email ?? ''} onChange={(e) => set({ email: e.target.value })} />
        </Labeled>
        <Labeled label="Phone">
          <input className="field" value={form.phone ?? ''} onChange={(e) => set({ phone: e.target.value })} />
        </Labeled>
        <Labeled label="Member type">
          <select
            className="field"
            value={form.memberType ?? ''}
            onChange={(e) => set({ memberType: e.target.value ? (e.target.value as MemberType) : null })}
          >
            <option value="">— not set —</option>
            {MEMBER_TYPES.map((t) => (
              <option key={t} value={t}>
                {MEMBER_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </Labeled>
        <Labeled label="Member since">
          <input className="field" type="date" value={form.memberSince ?? ''} onChange={(e) => set({ memberSince: e.target.value })} />
        </Labeled>
        <Labeled label="Account status">
          <select className="field" value={form.accountStatus ?? ''} onChange={(e) => set({ accountStatus: e.target.value })}>
            <option value="">— none —</option>
            {ACCOUNT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Labeled>
      </div>
      <Labeled label="Notes">
        <textarea className="field min-h-[70px]" value={form.notes ?? ''} onChange={(e) => set({ notes: e.target.value })} />
      </Labeled>
      <button type="submit" className="btn-primary" disabled={save.isPending}>
        {save.isPending ? 'Saving…' : 'Save changes'}
      </button>
    </form>
  );
}

function RolesCard({ member }: { member: MemberRow }) {
  const qc = useQueryClient();
  const update = useMutation({
    mutationFn: (roles: Role[]) => membersApi.update(member.id, { roles }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['members', member.id] });
      void qc.invalidateQueries({ queryKey: ['members'] });
    },
  });

  const roles = member.roles as Role[];
  function toggle(role: Role) {
    const has = roles.includes(role);
    const next = has ? roles.filter((r) => r !== role) : [...roles, role];
    if (next.length === 0) return; // must keep at least one role
    update.mutate(next);
  }

  return (
    <div className="card">
      <h2 className="mb-4 text-sm uppercase tracking-[0.15em] text-gold-400">Roles &amp; access</h2>
      <div className="flex flex-wrap gap-2">
        {ROLES.map((r) => {
          const active = roles.includes(r);
          return (
            <button
              key={r}
              onClick={() => toggle(r)}
              disabled={update.isPending}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                active ? 'bg-gold-600 text-on-gold' : 'border border-ink-800 text-cream-300/60 hover:border-gold-500'
              }`}
            >
              {ROLE_LABELS[r]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const STATUS_STYLES: Record<DuesStatus, string> = {
  pending: 'bg-wine-700 text-on-wine',
  paid: 'bg-gold-600 text-on-gold',
  waived: 'border border-ink-800 text-cream-300/60',
  cancelled: 'border border-ink-800 text-cream-300/40 line-through',
};

function DuesCard({ personId, readOnly = false }: { personId: string; readOnly?: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['members', personId, 'dues'], queryFn: () => membersApi.dues(personId) });
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['members', personId, 'dues'] });
    void qc.invalidateQueries({ queryKey: ['stats', 'overview'] });
  };

  const setStatus = useMutation({
    mutationFn: ({ dueId, status }: { dueId: string; status: DuesStatus }) => membersApi.updateDue(personId, dueId, { status }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (dueId: string) => membersApi.deleteDue(personId, dueId),
    onSuccess: invalidate,
  });

  return (
    <div className="card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm uppercase tracking-[0.15em] text-gold-400">Dues &amp; payments</h2>
        {data && (
          <div className="flex gap-4 text-xs">
            <span className="text-cream-300/60">
              Outstanding <span className="font-display text-sm text-wine-300">{money(data.summary.outstanding)}</span>
            </span>
            <span className="text-cream-300/60">
              Paid <span className="font-display text-sm text-gold-200">{money(data.summary.paid)}</span>
            </span>
          </div>
        )}
      </div>

      {!readOnly && <ChargeForm personId={personId} onDone={invalidate} />}

      {isLoading ? (
        <p className="mt-4 text-sm text-cream-300/50">Loading dues…</p>
      ) : !data || data.dues.length === 0 ? (
        <p className="mt-4 text-sm text-cream-300/50">No dues on record yet.</p>
      ) : (
        <ul className="mt-4 divide-y divide-ink-800">
          {data.dues.map((d) => (
            <DuesRow
              key={d.id}
              due={d}
              readOnly={readOnly}
              busy={setStatus.isPending || remove.isPending}
              onStatus={(status) => setStatus.mutate({ dueId: d.id, status })}
              onDelete={() => remove.mutate(d.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function DuesRow({
  due,
  readOnly,
  busy,
  onStatus,
  onDelete,
}: {
  due: DueRow;
  readOnly?: boolean;
  busy: boolean;
  onStatus: (s: DuesStatus) => void;
  onDelete: () => void;
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-cream-100">{due.label}</span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ${STATUS_STYLES[due.status]}`}>
            {due.status}
          </span>
        </div>
        <div className="mt-0.5 text-xs text-cream-300/50">
          {money(Number(due.amount))}
          {due.dueDate ? ` · due ${due.dueDate}` : ''}
          {due.status === 'paid' && due.paidAt ? ` · paid ${due.paidAt}` : ''}
        </div>
      </div>
      {!readOnly && (
      <div className="flex items-center gap-1">
        {due.status !== 'paid' && (
          <button onClick={() => onStatus('paid')} disabled={busy} className="rounded border border-gold-600/40 px-2 py-1 text-[11px] text-gold-200 hover:border-gold-400">
            Mark paid
          </button>
        )}
        {due.status === 'pending' && (
          <button onClick={() => onStatus('waived')} disabled={busy} className="rounded border border-ink-800 px-2 py-1 text-[11px] text-cream-300/60 hover:border-gold-500">
            Waive
          </button>
        )}
        {due.status === 'paid' && (
          <button onClick={() => onStatus('pending')} disabled={busy} className="rounded border border-ink-800 px-2 py-1 text-[11px] text-cream-300/60 hover:border-gold-500">
            Unpay
          </button>
        )}
        <button onClick={onDelete} disabled={busy} className="rounded border border-ink-800 px-2 py-1 text-[11px] text-cream-300/40 hover:border-wine-500 hover:text-wine-300">
          Delete
        </button>
      </div>
      )}
    </li>
  );
}

function ChargeForm({ personId, onDone }: { personId: string; onDone: () => void }) {
  const currentYear = new Date().getFullYear();
  const [label, setLabel] = useState(`${currentYear} Annual Dues`);
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [status, setStatus] = useState<DuesStatus>('pending');
  const [error, setError] = useState<string | null>(null);

  const charge = useMutation({
    mutationFn: () => {
      const body: ChargeDuesInput = {
        label: label.trim(),
        amount: Number(amount),
        periodYear: currentYear,
        dueDate: dueDate || null,
        status,
      };
      return membersApi.charge(personId, body);
    },
    onSuccess: () => {
      setAmount('');
      setDueDate('');
      setStatus('pending');
      setError(null);
      onDone();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not charge dues'),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!label.trim() || !amount || Number.isNaN(Number(amount))) {
      setError('Enter a label and amount.');
      return;
    }
    charge.mutate();
  }

  return (
    <form onSubmit={submit} className="rounded-lg border border-ink-800 bg-ink-950/40 p-3">
      {error && <p className="mb-2 text-sm text-wine-300">{error}</p>}
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <input className="field" placeholder="Description" value={label} onChange={(e) => setLabel(e.target.value)} />
        <input className="field" type="number" min="0" step="0.01" placeholder="Amount (USD)" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <input className="field" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} title="Due date" />
        <select className="field" value={status} onChange={(e) => setStatus(e.target.value as DuesStatus)}>
          {DUES_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s === 'pending' ? 'Charge (pending)' : s === 'paid' ? 'Record as paid' : s}
            </option>
          ))}
        </select>
      </div>
      <button type="submit" className="btn-primary mt-2 text-sm" disabled={charge.isPending}>
        {charge.isPending ? 'Saving…' : 'Charge dues'}
      </button>
    </form>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs uppercase tracking-wide text-cream-300/50">{label}</span>
      {children}
    </label>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-cream-300/50">{label}</dt>
      <dd className="mt-0.5 text-cream-100">{value || '—'}</dd>
    </div>
  );
}

/** A person in the records with no login yet — a past member, or someone created by an import. */
function InviteExistingCard({ member }: { member: MemberRow }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState(member.email ?? '');
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const invite = useMutation({
    mutationFn: () => membersApi.inviteExisting(member.id, { email: email.trim() || undefined }),
    onSuccess: (res) => {
      setLink(res.acceptUrl);
      setError(res.emailSent ? null : 'Email could not be delivered — share the link below.');
      void qc.invalidateQueries({ queryKey: ['members', member.id] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not send invitation'),
  });
  return (
    <div className="card border-gold-600/25 bg-ink-900/30">
      <h2 className="text-sm uppercase tracking-[0.15em] text-gold-400">No login yet</h2>
      <p className="mt-2 text-sm text-cream-300/70">
        {member.firstName} is in the records but has never had an account. Invite them to sign in — their tasting history comes with them.
        {member.memberType ? '' : ' They will be marked as a Past Member.'}
      </p>
      {error && <p className="mt-3 text-sm text-wine-300">{error}</p>}
      {link && (
        <p className="mt-3 break-all rounded border border-ink-800 bg-ink-950/50 p-2 font-mono text-xs text-cream-200">{link}</p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input className="field max-w-xs" type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button className="btn-primary" onClick={() => invite.mutate()} disabled={!email.trim() || invite.isPending}>
          {invite.isPending ? 'Inviting…' : 'Invite to create an account'}
        </button>
      </div>
    </div>
  );
}
